import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import LoadingSkeleton from '../components/LoadingSkeleton';

const GameListDetails = () => {
    const { id } = useParams();
    const { user, isAuthenticated } = useContext(AuthContext);
    const navigate = useNavigate();
    
    const [list, setList] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [liking, setLiking] = useState(false);
    const [following, setFollowing] = useState(false);

    useEffect(() => {
        fetchList();
    }, [id]);

    const fetchList = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('token');
            const config = token ? { headers: { 'Authorization': `Bearer ${token}` } } : {};
            
            const res = await axios.get(`/api/game-lists/${id}`, config);
            setList(res.data.data);
            setError('');
        } catch (err) {
            console.error(err);
            setError('Failed to load list details');
        } finally {
            setLoading(false);
        }
    };

    const handleLike = async () => {
        if (!isAuthenticated) {
            navigate('/login');
            return;
        }

        if (liking) return;
        setLiking(true);

        try {
            const token = localStorage.getItem('token');
            const res = await axios.put(`/api/game-lists/${id}/like`, {}, {
                headers: { 'Authorization': `Bearer ${token}` }
            });

            setList(prev => ({
                ...prev,
                likes: res.data.data.likes,
                likeCount: res.data.data.likeCount
            }));
        } catch (err) {
            console.error('Error liking list:', err);
        } finally {
            setLiking(false);
        }
    };

    const handleFollow = async () => {
        if (!isAuthenticated) {
            navigate('/login');
            return;
        }

        if (following) return;
        setFollowing(true);

        try {
            const token = localStorage.getItem('token');
            const res = await axios.put(`/api/game-lists/${id}/follow`, {}, {
                headers: { 'Authorization': `Bearer ${token}` }
            });

            setList(prev => ({
                ...prev,
                followers: res.data.data.followers,
                followerCount: res.data.data.followerCount
            }));
        } catch (err) {
            console.error('Error following list:', err);
        } finally {
            setFollowing(false);
        }
    };

    const handleDelete = async () => {
        if (!window.confirm('Are you sure you want to delete this list? This action cannot be undone.')) return;

        try {
            const token = localStorage.getItem('token');
            await axios.delete(`/api/game-lists/${id}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            navigate('/discover-lists');
        } catch (err) {
            console.error('Error deleting list:', err);
            alert('Failed to delete list');
        }
    };

    if (loading) return <LoadingSkeleton />;
    if (error) return <div className="container" style={{ padding: '40px', color: 'var(--error-text)', textAlign: 'center' }}>{error}</div>;
    if (!list) return <div className="container" style={{ padding: '40px', textAlign: 'center' }}>List not found</div>;

    const isOwner = user && list.owner && user._id === list.owner._id;
    const isLiked = user && list.likes && list.likes.includes(user._id);
    const isFollowed = user && list.followers && list.followers.includes(user._id);

    return (
        <div className="container" style={{ padding: '20px', maxWidth: '1000px', margin: '0 auto' }}>
            <div style={{ marginBottom: '30px', background: 'var(--card-bg)', padding: '30px', borderRadius: '16px', border: '1px solid var(--border-color)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div>
                        <h1 style={{ margin: '0 0 10px 0', color: 'var(--text-primary)' }}>{list.title}</h1>
                        <div style={{ display: 'flex', alignItems: 'center', marginBottom: '20px' }}>
                            <Link to={`/profile/${list.owner.username}`} style={{ display: 'flex', alignItems: 'center', textDecoration: 'none', color: 'inherit' }}>
                                {list.owner.profile?.avatar?.url ? (
                                    <img src={list.owner.profile.avatar.url} alt={list.owner.username} style={{ width: '40px', height: '40px', borderRadius: '50%', marginRight: '12px' }} />
                                ) : (
                                    <div style={{ width: '40px', height: '40px', borderRadius: '50%', background: '#555', marginRight: '12px' }}></div>
                                )}
                                <span style={{ color: 'var(--text-primary)', fontWeight: 'bold' }}>{list.owner.username}</span>
                            </Link>
                            <span style={{ margin: '0 10px', color: 'var(--text-secondary)' }}>•</span>
                            <span style={{ color: 'var(--text-secondary)' }}>{new Date(list.createdAt).toLocaleDateString()}</span>
                            {!list.isPublic && <span style={{ marginLeft: '10px', background: '#333', color: '#ccc', padding: '2px 8px', borderRadius: '4px', fontSize: '0.8rem' }}>Private</span>}
                        </div>
                        <p style={{ color: 'var(--text-secondary)', fontSize: '1.1rem', lineHeight: '1.6' }}>{list.description}</p>
                        
                        {list.tags && list.tags.length > 0 && (
                            <div style={{ display: 'flex', gap: '8px', marginTop: '15px' }}>
                                {list.tags.map((tag, index) => (
                                    <span key={index} style={{ background: 'var(--bg-secondary)', padding: '4px 12px', borderRadius: '20px', fontSize: '0.9rem', color: 'var(--text-secondary)' }}>
                                        #{tag}
                                    </span>
                                ))}
                            </div>
                        )}
                    </div>

                    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                        <button 
                            onClick={handleLike}
                            style={{ 
                                display: 'flex', 
                                alignItems: 'center', 
                                gap: '8px', 
                                padding: '8px 16px', 
                                borderRadius: '8px', 
                                border: '1px solid var(--border-color)', 
                                background: isLiked ? 'rgba(255, 0, 0, 0.1)' : 'transparent',
                                color: isLiked ? '#ff4d4d' : 'var(--text-primary)',
                                cursor: 'pointer',
                                transition: 'all 0.2s'
                            }}
                        >
                            <span style={{ fontSize: '1.2rem' }}>{isLiked ? '❤️' : '🤍'}</span>
                            <span>{list.likeCount} Likes</span>
                        </button>

                        <button 
                            onClick={handleFollow}
                            style={{ 
                                display: 'flex', 
                                alignItems: 'center', 
                                gap: '8px', 
                                padding: '8px 16px', 
                                borderRadius: '8px', 
                                border: '1px solid var(--border-color)', 
                                background: isFollowed ? 'rgba(76, 175, 80, 0.1)' : 'transparent',
                                color: isFollowed ? '#4CAF50' : 'var(--text-primary)',
                                cursor: 'pointer',
                                transition: 'all 0.2s'
                            }}
                        >
                            <span style={{ fontSize: '1.2rem' }}>{isFollowed ? '✓' : '+'}</span>
                            <span>{isFollowed ? 'Following' : 'Follow'}</span>
                        </button>

                        {isOwner && (
                            <>
                                <button 
                                    onClick={() => navigate(`/lists/${id}/edit`)}
                                    style={{ 
                                        padding: '8px 16px', 
                                        borderRadius: '8px', 
                                        border: '1px solid var(--primary-color)', 
                                        background: 'transparent',
                                        color: 'var(--primary-color)',
                                        cursor: 'pointer'
                                    }}
                                >
                                    Edit List
                                </button>
                                <button 
                                    onClick={handleDelete}
                                    style={{ 
                                        padding: '8px 16px', 
                                        borderRadius: '8px', 
                                        border: '1px solid var(--error-border)', 
                                        background: 'transparent',
                                        color: 'var(--error-text)',
                                        cursor: 'pointer'
                                    }}
                                >
                                    Delete List
                                </button>
                            </>
                        )}
                    </div>
                </div>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                {list.games.map((item, index) => (
                    <div key={index} style={{ 
                        display: 'flex', 
                        background: 'var(--card-bg)', 
                        borderRadius: '12px', 
                        overflow: 'hidden',
                        border: '1px solid var(--border-color)'
                    }}>
                        <div style={{ 
                            width: '40px', 
                            background: 'var(--bg-secondary)', 
                            display: 'flex', 
                            alignItems: 'center', 
                            justifyContent: 'center',
                            fontSize: '1.2rem',
                            fontWeight: 'bold',
                            color: 'var(--text-secondary)',
                            flexShrink: 0
                        }}>
                            {index + 1}
                        </div>
                        <div style={{ width: '150px', flexShrink: 0 }}>
                            <img 
                                src={item.game.coverImage || '/placeholder-game.jpg'} 
                                alt={item.game.name} 
                                style={{ width: '100%', height: '100%', objectFit: 'cover' }} 
                            />
                        </div>
                        <div style={{ padding: '20px', flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                            <h3 style={{ margin: '0 0 10px 0' }}>
                                <Link to={`/game/${item.game._id}`} style={{ color: 'var(--text-primary)', textDecoration: 'none' }}>
                                    {item.game.name}
                                </Link>
                            </h3>
                            {item.note && (
                                <div style={{ 
                                    background: 'var(--bg-secondary)', 
                                    padding: '12px', 
                                    borderRadius: '8px', 
                                    borderLeft: '4px solid var(--primary-color)',
                                    fontStyle: 'italic',
                                    color: 'var(--text-secondary)'
                                }}>
                                    "{item.note}"
                                </div>
                            )}
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

export default GameListDetails;
